import { LoadingButton } from '@mui/lab';
import {
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    Typography,
    Alert,
    FormControl,
    TextField,
    Select,
    MenuItem,
} from '@mui/material';
import { FC, useState } from 'react';
import { toast } from 'react-toastify';
import CloseIcon from '@mui/icons-material/Close';
import { AddPlusIcon } from '../../../vectors/index';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useInviteCompany } from '../hooks/useInviteCompany';
import { useGetTemplates } from '../hooks/useGetTemplates';

type TemplatesDropdownModalProps = {
    handleClose: () => void;
};

const schema = yup.object({
    email: yup.string().email('Invalid email address').required('Email is required'),
}).required();

const TemplatesDropdownModal: FC<TemplatesDropdownModalProps> = ({ handleClose }) => {
    const [selectedTemplate, setSelectedTemplete] = useState<number | string>('');
    const [errorText, setErrorText] = useState<string>('');

    const { templatesList, isLoadingTemplatesList } = useGetTemplates({
        setErrorText,
        searchValue: '',
        status: 0
    });

    const { inviteCompany, isInvitingCompany } = useInviteCompany({ setErrorText });

    const { control, handleSubmit, formState: { errors } } = useForm({
        resolver: yupResolver(schema)
    });

    const onSubmit = (data: { email: string; }) => {
        const options = {
            onSuccess: (data: { message: string }) => {
                toast.success(data.message);
                handleClose();
            },
        };
        inviteCompany({ ...data, etemplate_id: selectedTemplate }, options);
    };

    return (
      <Dialog open onClose={handleClose} maxWidth="xs" fullWidth>
        <DialogTitle>
          <IconButton
            sx={{ position: 'absolute', top: 5, right: 5 }}
            onClick={handleClose}
          >
            <CloseIcon />
          </IconButton>
          <br />
          <AddPlusIcon />
          <br />
          <Typography variant="h4">Invite Company</Typography>
        </DialogTitle>
        <DialogContent>
          {errorText && (
            <Alert sx={{ margin: '10px 0' }} severity="error">
              {errorText}
            </Alert>
          )}

          <FormControl fullWidth>
            <Typography id="pay-rate-type-label">Select Template</Typography>
            <Select
              value={selectedTemplate}
              onChange={(e) => setSelectedTemplete(e.target.value)}
              displayEmpty
              size="small"
              MenuProps={{ PaperProps: { style: { borderRadius: 5 } } }}
            >
              <MenuItem value="" disabled>
                Select Template
              </MenuItem>
              {templatesList?.object?.map(
                (template: { id: number; name: string }) => (
                  <MenuItem key={template.id} value={template.id}>
                    {template.name}
                  </MenuItem>
                ),
              )}
            </Select>
          </FormControl>

          <form onSubmit={handleSubmit(onSubmit)}>
            <FormControl fullWidth margin="normal">
              <Controller
                name="email"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Email"
                    size="small"
                    variant="outlined"
                    error={!!errors.email}
                    helperText={errors.email ? errors.email.message : ''}
                    fullWidth
                  />
                )}
              />
            </FormControl>

            <LoadingButton
              type="submit"
              variant="contained"
              color="primary"
              loading={isInvitingCompany}
              disabled={!selectedTemplate || isInvitingCompany}
              fullWidth
              sx={{ marginTop: 2, marginBottom: 2 }}
            >
              Submit
            </LoadingButton>
          </form>
        </DialogContent>
      </Dialog>
    );
};

export default TemplatesDropdownModal;
